import { useEffect, useState } from "react";
import { FileSpreadsheet, Pencil, Plus, Shapes, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { EMPTY_SHAPE, ShapeEditor } from "@/components/settings/ShapeEditor";
import { ShapeThumb } from "@/components/settings/ShapeThumb";
import { SupplierShapesDialog } from "@/components/settings/SupplierShapesDialog";
import { styleLabel } from "@/lib/components";
import { confirmSheet } from "@/components/ui/action-sheet";

const btn = "inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-200";

const appliesText = (s, lines, types) => {
  if (s.applies_all) return "All product lines";
  const n = s.line_ids.length, t = types.filter((x) => s.type_ids.includes(x.id)).map((x) => x.name);
  const parts = [n ? `${n} product line${n === 1 ? "" : "s"}` : "", t.length ? `types: ${t.join(", ")}` : ""].filter(Boolean);
  return parts.join(" · ") || "Not applied yet";
};

export function ShapesSection({ types }) {
  const [shapes, setShapes] = useState(null);
  const [lines, setLines] = useState([]);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => Promise.all([api.get("/settings/shapes"), api.get("/settings/product-lines")])
    .then(([s, l]) => { setShapes(s.data); setLines(l.data); }).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const del = async (s) => {
    if (!(await confirmSheet({ title: `Delete shape "${s.name}"?`, message: "Saved quotes keep their prices.", confirm: "Delete Shape", destructive: true }))) return;
    try {
      await api.delete(`/settings/shapes/${s.id}`);
      toast.success("Shape deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <section data-testid="shapes-section" className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-100"><Shapes className="h-4 w-4 text-amber-300" />
            <h2 className="font-display text-base font-bold md:text-lg">Shapes · arch &amp; custom</h2></div>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">Set each shape up once: picture, drawing, price and where it applies. Lines on matching product lines can then pick it.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="shapes-load-supplier-button" onClick={() => setLoading(true)} className={btn}><FileSpreadsheet className="h-4 w-4" /> Load shapes from supplier list</button>
          <button data-testid="shapes-add-button" onClick={() => setEdit(EMPTY_SHAPE)} className={btn}><Plus className="h-4 w-4" /> Add shape</button>
        </div>
      </div>
      <div className="mt-5 space-y-2">
        {shapes && shapes.length === 0 && <p data-testid="shapes-empty" className="text-sm text-slate-500">No shapes yet.</p>}
        {(shapes || []).map((s, i) => (
          <div key={s.id} data-testid={`shape-row-${i}`} className="flex items-center gap-4 rounded-xl border border-slate-800/70 px-3 py-2 transition-colors hover:bg-slate-900/40">
            <ShapeThumb shape={s} testId={`shape-picture-${i}`} />
            <div className="min-w-0 flex-1">
              <p data-testid={`shape-name-${i}`} className="truncate text-sm font-semibold text-slate-100">{s.name}</p>
              <p className="text-xs text-slate-400">{styleLabel(s.style)} · {appliesText(s, lines, types)}</p>
            </div>
            <span data-testid={`shape-price-${i}`} className="whitespace-nowrap font-mono-num text-sm text-slate-200">${Number(s.price).toFixed(2)}{s.unit === "sqft" ? "/sq ft" : "/piece"}</span>
            <button aria-label="Edit" data-testid={`shape-edit-${i}`} onClick={() => setEdit(s)} className="p-1 text-slate-400 hover:text-amber-200"><Pencil className="h-4 w-4" /></button>
            <button aria-label="Delete" data-testid={`shape-delete-${i}`} onClick={() => del(s)} className="p-1 text-slate-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {edit && <ShapeEditor shape={edit} lines={lines} types={types} onClose={() => setEdit(null)} onSaved={load} />}
      {loading && <SupplierShapesDialog lines={lines} onClose={() => setLoading(false)} onDone={load} />}
    </section>
  );
}
